import React, { useState, useEffect, useCallback } from 'react';
import './MicropousseForm.css';

/* ── Données statiques ───────────────────────────────────────────── */

const PRODUITS = [
  { key: 'micropoussesPlateau',  label: 'Micropousses (plateaux vivants)' },
  { key: 'micropoussesCoupees', label: 'Micropousses (coupées, emballées)' },
  { key: 'fleursComestibles',   label: 'Fleurs comestibles' },
  { key: 'legumesMiniatures',   label: 'Légumes miniatures / bébé' },
  { key: 'legumesRares',        label: 'Légumes rares / saison' },
];

const CLASSEMENTS = [
  'Restaurant gastronomique (sans étoile)',
  '1 étoile Michelin',
  '2 étoiles Michelin',
  '3 étoiles Michelin',
  'Bib Gourmand',
  'Autre',
];

const LABELS_ENGAGEMENTS = [
  'Maître Restaurateur',
  'Logo AB (avec % indiqué)',
  'Écotable',
  'Étoile Verte',
  'Label Vegoresto (L214)',
];

const SOURCES = [
  "Marché d'intérêt national (MIN)",
  'Grossiste spécialisé',
  'Producteur local (circuit court)',
  'Je les cultive moi-même / en cuisine',
  'Importateur / plateforme nationale',
  'Autre',
];

const INITIAL = {
  nomRestaurant: '', adresse: '', chefCuisine: '', nombreCouverts: '',
  classement: [], autreClassement: '', labelsEngagements: [], gammesPrix: '',
  utiliseMicropousses: '',
  sourceApprovisionnement: [], autreSource: '', satisfaction: '', manqueActuel: '',
  produits: {
    micropoussesPlateau:  { interet: '', volume: '', frequence: '' },
    micropoussesCoupees:  { interet: '', volume: '', frequence: '' },
    fleursComestibles:    { interet: '', volume: '', frequence: '' },
    legumesMiniatures:    { interet: '', volume: '', frequence: '' },
    legumesRares:         { interet: '', volume: '', frequence: '' },
  },
  varietesSpecifiques: '', preferenceReception: '',
  frequenceLivraison: '', creneauLivraison: '', circuitCourt: '', carteProduceurs: '',
  prixMicropousses: '', prixFleurs: '', abonnement: '',
  interetPartenariat: '', disponibleEchange: '', disponibilites: '', remarques: '',
  nomPrenom: '', poste: '', emailContact: '', telephone: '',
};

const STORAGE_KEY = 'mf_draft';

/* ── Sous-composants ─────────────────────────────────────────────── */

function RadioGroup({ name, options, value, onChange }) {
  return (
    <div className="mf-radio-group">
      {options.map(opt => (
        <label key={opt} className="mf-choice-label">
          <input type="radio" name={name} value={opt}
            checked={value === opt} onChange={() => onChange(opt)} />
          <span className="mf-choice-mark" />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  );
}

function CheckboxGroup({ options, values, onChange }) {
  const toggle = (opt, checked) =>
    onChange(checked ? [...values, opt] : values.filter(v => v !== opt));
  return (
    <div className="mf-checkbox-group">
      {options.map(opt => (
        <label key={opt} className="mf-choice-label">
          <input type="checkbox" checked={values.includes(opt)}
            onChange={e => toggle(opt, e.target.checked)} />
          <span className="mf-choice-mark mf-choice-mark--square" />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  );
}

function SectionHeader({ num, title }) {
  return (
    <div className="mf-section-head">
      <span className="mf-section-num">{num}</span>
      <h2 className="mf-section-title">{title}</h2>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="mf-field">
      <label className="mf-label">
        {label}
        {hint && <span className="mf-hint"> — {hint}</span>}
      </label>
      {children}
    </div>
  );
}

/* ── Composant principal ─────────────────────────────────────────── */

export default function MicropousseForm() {
  const urlToken = new URLSearchParams(window.location.search).get('token') || '';
  const [form, setForm] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...INITIAL, ...JSON.parse(saved) } : INITIAL;
    } catch { return INITIAL; }
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  /* Persistance brouillon */
  useEffect(() => {
    document.title = 'Etude de marché - micropousses';
    return () => { document.title = 'Monke Art Flow'; };
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)); } catch {}
  }, [form]);

  const set = useCallback((field, value) =>
    setForm(f => ({ ...f, [field]: value })), []);

  const setProduit = useCallback((key, sub, value) =>
    setForm(f => ({
      ...f,
      produits: { ...f.produits, [key]: { ...f.produits[key], [sub]: value } },
    })), []);

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitError(null);
    try {
      const res = await fetch('/api/fast/micropousse/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, token: urlToken }),
      });
      if (!res.ok) throw new Error(`Erreur serveur (${res.status})`);
    } catch (err) {
      setSubmitError(err.message || 'Erreur réseau, veuillez réessayer.');
      return;
    }
    localStorage.removeItem(STORAGE_KEY);
    setForm(INITIAL);
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* ── Écran de confirmation ──────────────────────────────────────── */
  if (submitted) return (
    <div className="mf-page">
      <header className="mf-header" aria-label="En-tête">
        <HeaderInner />
      </header>
      <div className="mf-success">
        <div className="mf-success-card">
          <span className="mf-ornament" aria-hidden>✦</span>
          <h2>Merci pour vos réponses</h2>
          <p>Vos réponses ont bien été enregistrées.</p>
          <p className="mf-success-note">Antoine Cailliez vous recontactera prochainement.</p>
          <div className="mf-success-actions">
            <button className="mf-btn mf-btn--outline" onClick={() => setSubmitted(false)}>
              Nouveau formulaire
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );

  /* ── Formulaire principal ───────────────────────────────────────── */
  const useCurrent = form.utiliseMicropousses === 'Oui, régulièrement'
                  || form.utiliseMicropousses === 'Oui, occasionnellement';

  return (
    <div className="mf-page">
      <header className="mf-header">
        <HeaderInner />
      </header>

      <form className="mf-form" onSubmit={handleSubmit} noValidate>

        {/* ── 01 Établissement ── */}
        <section className="mf-section">
          <SectionHeader num="01" title="Votre établissement" />

          <Field label="Nom du restaurant">
            <input type="text" value={form.nomRestaurant}
              onChange={e => set('nomRestaurant', e.target.value)} placeholder="…" />
          </Field>

          <Field label="Adresse">
            <input type="text" value={form.adresse}
              onChange={e => set('adresse', e.target.value)} placeholder="…" />
          </Field>

          <div className="mf-row">
            <Field label="Chef de cuisine">
              <input type="text" value={form.chefCuisine}
                onChange={e => set('chefCuisine', e.target.value)} placeholder="…" />
            </Field>
            <Field label="Couverts / service">
              <input type="number" value={form.nombreCouverts} min="1"
                onChange={e => set('nombreCouverts', e.target.value)} placeholder="…" />
            </Field>
          </div>

          <Field label="Classement & distinctions">
            <CheckboxGroup options={CLASSEMENTS}
              values={form.classement}
              onChange={v => set('classement', v)} />
            {form.classement.includes('Autre') && (
              <input className="mf-sub-input" type="text" placeholder="Précisez…"
                value={form.autreClassement}
                onChange={e => set('autreClassement', e.target.value)} />
            )}
          </Field>

          <Field label="Labels & engagements">
            <CheckboxGroup options={LABELS_ENGAGEMENTS}
              values={form.labelsEngagements}
              onChange={v => set('labelsEngagements', v)} />
          </Field>

          <Field label="Gamme de prix — menu dégustation / gastronomique">
            <RadioGroup name="gammesPrix"
              options={['Moins de 50 €', '50 € – 100 €', '100 € – 150 €', 'Plus de 150 €']}
              value={form.gammesPrix} onChange={v => set('gammesPrix', v)} />
          </Field>
        </section>

        {/* ── 02 Approvisionnement ── */}
        <section className="mf-section">
          <SectionHeader num="02" title="Approvisionnement actuel" />

          <Field label="Utilisez-vous des micropousses, fleurs comestibles ou légumes miniatures dans vos assiettes ?">
            <RadioGroup name="utilise"
              options={['Oui, régulièrement', 'Oui, occasionnellement', 'Non, mais intéressé(e)', "Non, pas d'intérêt"]}
              value={form.utiliseMicropousses} onChange={v => set('utiliseMicropousses', v)} />
          </Field>

          {useCurrent && (
            <div className="mf-animate-in">
              <Field label="Où vous approvisionnez-vous ?" hint="plusieurs réponses possibles">
                <CheckboxGroup options={SOURCES}
                  values={form.sourceApprovisionnement}
                  onChange={v => set('sourceApprovisionnement', v)} />
                {form.sourceApprovisionnement.includes('Autre') && (
                  <input className="mf-sub-input" type="text" placeholder="Précisez…"
                    value={form.autreSource}
                    onChange={e => set('autreSource', e.target.value)} />
                )}
              </Field>
            </div>
          )}

          <Field label="Satisfaction vis-à-vis de votre approvisionnement actuel">
            <RadioGroup name="satisfaction"
              options={[
                'Très satisfait(e)',
                'Plutôt satisfait(e)',
                'Peu satisfait(e) — manque de fraîcheur / régularité',
                'Pas satisfait(e) — gamme trop limitée',
              ]}
              value={form.satisfaction} onChange={v => set('satisfaction', v)} />
          </Field>

          <Field label="Ce que vous ne trouvez pas facilement aujourd'hui">
            <textarea rows={3} value={form.manqueActuel}
              onChange={e => set('manqueActuel', e.target.value)}
              placeholder="Décrivez librement vos difficultés d'approvisionnement actuelles…" />
          </Field>
        </section>

        {/* ── 03 Produits & volumes ── */}
        <section className="mf-section">
          <SectionHeader num="03" title="Produits souhaités & volumes" />

          <Field label="Estimez vos besoins approximatifs">
            <div className="mf-table-wrap">
              <table className="mf-table">
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th>Intérêt</th>
                    <th>Volume / semaine</th>
                    <th>Livraisons / sem.</th>
                  </tr>
                </thead>
                <tbody>
                  {PRODUITS.map(({ key, label }) => (
                    <tr key={key}>
                      <td>{label}</td>
                      <td>
                        <select value={form.produits[key].interet}
                          onChange={e => setProduit(key, 'interet', e.target.value)}>
                          <option value="">—</option>
                          <option>Oui</option>
                          <option>Non</option>
                          <option>À voir</option>
                        </select>
                      </td>
                      <td>
                        <input type="text" placeholder="ex : 200 g"
                          value={form.produits[key].volume}
                          onChange={e => setProduit(key, 'volume', e.target.value)} />
                      </td>
                      <td>
                        <select value={form.produits[key].frequence}
                          onChange={e => setProduit(key, 'frequence', e.target.value)}>
                          <option value="">—</option>
                          <option value="1">1×</option>
                          <option value="2">2×</option>
                          <option value="3">3×</option>
                          <option value="4+">4×+</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Field>

          <Field label="Variétés ou produits spécifiques qui vous intéressent">
            <textarea rows={3} value={form.varietesSpecifiques}
              onChange={e => set('varietesSpecifiques', e.target.value)}
              placeholder="ex : shiso rouge, pousses de pois, cucamelon, oca du Pérou, oxalis, amarante, bourrache…" />
          </Field>

          <Field label="Préférez-vous recevoir vos micropousses">
            <RadioGroup name="preferenceReception"
              options={[
                'En plateaux vivants (cisaillées par vos soins — fraîcheur maximale)',
                "Coupées et conditionnées (prêtes à l'emploi)",
                'Les deux selon le produit',
                'Sans préférence',
              ]}
              value={form.preferenceReception} onChange={v => set('preferenceReception', v)} />
          </Field>
        </section>

        {/* ── 04 Logistique ── */}
        <section className="mf-section">
          <SectionHeader num="04" title="Logistique & livraison" />

          <Field label="Fréquence de livraison souhaitée">
            <RadioGroup name="frequenceLivraison"
              options={['1 fois par semaine', '2 fois par semaine', '3 fois par semaine ou plus', 'Selon les besoins / à la commande']}
              value={form.frequenceLivraison} onChange={v => set('frequenceLivraison', v)} />
          </Field>

          <Field label="Créneau de livraison idéal">
            <RadioGroup name="creneauLivraison"
              options={["Tôt le matin (avant 8h)", 'Matinée (8h – 12h)', "Début d'après-midi (12h – 15h)", 'Flexible / peu importe']}
              value={form.creneauLivraison} onChange={v => set('creneauLivraison', v)} />
          </Field>

          <Field label="Le circuit court et l'origine locale sont-ils des critères importants ?">
            <RadioGroup name="circuitCourt"
              options={[
                "Oui, c'est un critère de sélection majeur",
                'Oui, si la qualité et le prix sont équivalents',
                'Peu important par rapport à la qualité',
                'Non, pas de critère géographique',
              ]}
              value={form.circuitCourt} onChange={v => set('circuitCourt', v)} />
          </Field>

          <Field label="Avez-vous une carte / menu valorisant les producteurs locaux ?">
            <RadioGroup name="carteProduceurs"
              options={['Oui', 'Non', 'En projet']}
              value={form.carteProduceurs} onChange={v => set('carteProduceurs', v)} />
          </Field>
        </section>

        {/* ── 05 Budget ── */}
        <section className="mf-section">
          <SectionHeader num="05" title="Budget & tarification" />

          <Field label="Fourchette de prix — micropousses" hint="barquette 50–100 g, coupées">
            <RadioGroup name="prixMicropousses"
              options={['Moins de 3 € / barquette', '3 € – 5 € / barquette', '5 € – 8 € / barquette', 'Plus de 8 € si qualité / rareté justifiée']}
              value={form.prixMicropousses} onChange={v => set('prixMicropousses', v)} />
          </Field>

          <Field label="Fourchette de prix — fleurs comestibles" hint="petit plateau 20–30 fleurs">
            <RadioGroup name="prixFleurs"
              options={['Moins de 4 €', '4 € – 7 €', '7 € – 12 €', 'Plus de 12 € (variétés rares)']}
              value={form.prixFleurs} onChange={v => set('prixFleurs', v)} />
          </Field>

          <Field label="Commande hebdomadaire régulière (abonnement) en échange d'un tarif préférentiel ?">
            <RadioGroup name="abonnement"
              options={['Oui, très intéressé(e)', 'Peut-être, à discuter', 'Non, je préfère commander à la demande']}
              value={form.abonnement} onChange={v => set('abonnement', v)} />
          </Field>
        </section>

        {/* ── 06 Partenariat ── */}
        <section className="mf-section">
          <SectionHeader num="06" title="Partenariat & suite" />

          <Field label="Seriez-vous intéressé(e) par un partenariat durable avec un producteur local basé à Bayeux ?">
            <RadioGroup name="interetPartenariat"
              options={[
                "Oui, je suis très ouvert(e) à discuter",
                'Oui, si les produits correspondent à mes attentes',
                "Peut-être — je souhaite d'abord tester les produits",
                'Non merci',
              ]}
              value={form.interetPartenariat} onChange={v => set('interetPartenariat', v)} />
          </Field>

          <Field label="Seriez-vous disponible pour un échange de 20 minutes (café / visite de la ferme) ?">
            <RadioGroup name="disponibleEchange"
              options={['Oui', "Non pour l'instant"]}
              value={form.disponibleEchange} onChange={v => set('disponibleEchange', v)} />
          </Field>

          {form.disponibleEchange === 'Oui' && (
            <div className="mf-animate-in">
              <Field label="Vos disponibilités / créneau préféré">
                <input type="text" value={form.disponibilites}
                  onChange={e => set('disponibilites', e.target.value)}
                  placeholder="ex : mardis matin, semaine du 15 mars…" />
              </Field>
            </div>
          )}

          <Field label="Remarques, souhaits ou produits spécifiques">
            <textarea rows={4} value={form.remarques}
              onChange={e => set('remarques', e.target.value)}
              placeholder="Vos commentaires libres…" />
          </Field>
        </section>

        {/* ── Coordonnées ── */}
        <section className="mf-section mf-section--contact">
          <SectionHeader num="✦" title="Vos coordonnées pour le suivi" />

          <div className="mf-row">
            <Field label="Nom & prénom">
              <input type="text" value={form.nomPrenom}
                onChange={e => set('nomPrenom', e.target.value)} placeholder="…" />
            </Field>
            <Field label="Poste / fonction">
              <input type="text" value={form.poste}
                onChange={e => set('poste', e.target.value)} placeholder="…" />
            </Field>
          </div>

          <div className="mf-row">
            <Field label="Email">
              <input type="email" value={form.emailContact}
                onChange={e => set('emailContact', e.target.value)} placeholder="…" />
            </Field>
            <Field label="Téléphone">
              <input type="tel" value={form.telephone}
                onChange={e => set('telephone', e.target.value)} placeholder="…" />
            </Field>
          </div>
        </section>

        {/* ── Soumission ── */}
        <div className="mf-submit-area">
          <p className="mf-legal">
            Questionnaire strictement confidentiel — réponses utilisées uniquement dans le cadre de cette étude de marché.
          </p>
          {submitError && <p className="mf-error">{submitError}</p>}
          <button type="submit" className="mf-btn mf-btn--primary">
            Envoyer mes réponses
          </button>
        </div>
      </form>

      <Footer />
    </div>
  );
}

/* ── Fragments réutilisables ─────────────────────────────────────── */

function HeaderInner() {
  return (
    <div className="mf-header-inner">
      <div className="mf-header-top">
        <span className="mf-ornament" aria-hidden>✦</span>
        <div>
          <h1 className="mf-header-title">Antoine Cailliez</h1>
          <p className="mf-header-sub">Producteur maraîcher en cours d'installation · Bayeux, Calvados</p>
        </div>
      </div>
      <p className="mf-header-desc">
        Étude de marché — Micropousses, légumes miniatures & fleurs comestibles · 2026
      </p>
    </div>
  );
}

function Footer() {
  return (
    <footer className="mf-footer">
      <p>Antoine Cailliez · <a href="mailto:acailliez@gmail.com">acailliez@gmail.com</a> · Bayeux, Calvados (14)</p>
    </footer>
  );
}
